import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { Mail, User, Calendar, MessageSquare, Eye, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const ContactRequests = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/contact-requests`);
      setRequests(res.data || []);
    } catch (error) {
      toast.error('Failed to fetch contact requests');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (request) => {
    setSelectedRequest(request);
    setViewDialogOpen(true);
  };

  const handleDelete = async (requestId) => {
    if (!confirm('Are you sure you want to delete this request?')) return;
    
    try {
      await axios.delete(`${API_URL}/contact-requests/${requestId}`);
      toast.success('Request deleted');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to delete request');
    }
  };

  const handleMarkResolved = async (requestId) => {
    try {
      await axios.put(`${API_URL}/contact-requests/${requestId}`, { status: 'resolved' });
      toast.success('Marked as resolved');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'resolved':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Resolved</span>;
      case 'in_progress':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">In Progress</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Pending</span>;
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8" data-testid="contact-requests-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Contact Requests
          </h1>
          <p className="text-zinc-600">
            View and manage support requests from the AI chat widget
          </p>
        </div>

        {/* Info Box */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">📬 How Contact Requests Work</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• When the AI cannot answer a question, visitors can submit a contact form</li>
            <li>• Contact form collects: <strong>Name, Email, Message</strong>, and chat context</li>
            <li>• Requests are sent to: <strong>Settings → Contact Email</strong> (configure in Workspace Settings)</li>
            <li>• All requests are also stored here for your review</li>
          </ul>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">{t('loading')}</div>
        ) : requests.length > 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Contact</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Message</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-zinc-900">Date</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-zinc-900">Status</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-zinc-900">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center">
                          <User className="h-5 w-5 text-violet-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-zinc-900">{req.name}</div>
                          <div className="text-sm text-zinc-500">{req.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-zinc-700 truncate max-w-xs">{req.message}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-600">
                      {new Date(req.created_at).toLocaleDateString()}<br />
                      <span className="text-xs text-zinc-400">{new Date(req.created_at).toLocaleTimeString()}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {getStatusBadge(req.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleView(req)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {req.status !== 'resolved' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkResolved(req.id)}
                            className="text-green-600 hover:bg-green-50"
                          >
                            ✓
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(req.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-zinc-200 rounded-lg">
            <Mail className="h-16 w-16 mx-auto mb-4 text-zinc-300" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">No Contact Requests Yet</h3>
            <p className="text-zinc-600">
              When visitors submit contact forms from the AI chat, they'll appear here.
            </p>
          </div>
        )}

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Contact Request Details</DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-zinc-600">Name</label>
                    <p className="text-zinc-900">{selectedRequest.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-zinc-600">Email</label>
                    <p className="text-zinc-900">
                      <a href={`mailto:${selectedRequest.email}`} className="text-violet-600 hover:underline">
                        {selectedRequest.email}
                      </a>
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-600">Message</label>
                  <p className="text-zinc-900 whitespace-pre-wrap bg-zinc-50 p-3 rounded-lg mt-1">
                    {selectedRequest.message}
                  </p>
                </div>
                {selectedRequest.chat_context && (
                  <div>
                    <label className="text-sm font-medium text-zinc-600">Chat Context</label>
                    <div className="bg-zinc-50 p-3 rounded-lg mt-1 max-h-48 overflow-y-auto">
                      {(() => {
                        try {
                          const context = JSON.parse(selectedRequest.chat_context);
                          return context.map((msg, idx) => (
                            <div key={idx} className={`text-sm mb-2 ${msg.role === 'user' ? 'text-blue-700' : 'text-zinc-600'}`}>
                              <strong>{msg.role === 'user' ? 'User:' : 'AI:'}</strong> {msg.content}
                            </div>
                          ));
                        } catch {
                          return <p className="text-sm text-zinc-600">{selectedRequest.chat_context}</p>;
                        }
                      })()}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 pt-4">
                  <a
                    href={`mailto:${selectedRequest.email}?subject=Re: Your support request&body=Hi ${selectedRequest.name},%0D%0A%0D%0AThank you for reaching out.%0D%0A%0D%0A`}
                    className="flex-1"
                  >
                    <Button className="w-full bg-violet-600 hover:bg-violet-700">
                      <Mail className="h-4 w-4 mr-2" />
                      Reply via Email
                    </Button>
                  </a>
                  {selectedRequest.status !== 'resolved' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleMarkResolved(selectedRequest.id);
                        setViewDialogOpen(false);
                      }}
                    >
                      Mark Resolved
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ContactRequests;
